'use client';

import { useEffect, useRef, useState } from 'react';
import 'plyr/dist/plyr.css';

function formatVttTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, `0`)}:${String(m).padStart(2, `0`)}:${s.toFixed(3).padStart(6, `0`)}`;
}

function getChapterAt(chapters: { time: number; label: string }[], time: number): string {
  for (let i = chapters.length - 1; i >= 0; i--) {
    if (time >= chapters[i].time) return chapters[i].label;
  }
  return ``;
}

function setupChapterLabel(plyrEl: HTMLElement, video: HTMLVideoElement, chapters: { time: number; label: string }[], duration: number) {
  const playBtn = plyrEl.querySelector(`.plyr__controls > .plyr__control[data-plyr="play"]`) as HTMLElement;
  if (!playBtn) return () => {};

  const seekInput = plyrEl.querySelector(`input[data-plyr="seek"]`) as HTMLInputElement;

  const label = document.createElement(`span`);
  label.className = `chapter-label`;
  playBtn.after(label);

  let last = ``;
  const update = (time: number) => {
    const text = getChapterAt(chapters, time);
    if (text !== last) {
      label.textContent = text;
      last = text;
    }
  };

  const progress = plyrEl.querySelector(`.plyr__progress`) as HTMLElement;
  let chapterSpan: HTMLSpanElement | null = null;

  const onTimeUpdate = () => update(video.currentTime);
  const onSeekInput = () => update((parseFloat(seekInput.value) / 100) * duration);

  const onProgressMove = (e: MouseEvent) => {
    if (!progress || !duration) return;
    const rect = progress.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const time = pct * duration;
    const chapter = getChapterAt(chapters, time);
    if (!chapterSpan) {
      const timeContainer = plyrEl.querySelector(`.plyr__preview-thumb__time-container`) as HTMLElement;
      if (timeContainer) {
        chapterSpan = document.createElement(`span`);
        chapterSpan.className = `chapter-tooltip`;
        timeContainer.appendChild(chapterSpan);
      }
    }
    if (chapterSpan) {
      chapterSpan.textContent = chapter || ``;
    }
  };

  video.addEventListener(`timeupdate`, onTimeUpdate);
  seekInput?.addEventListener(`input`, onSeekInput);
  progress?.addEventListener(`mousemove`, onProgressMove);

  return () => {
    video.removeEventListener(`timeupdate`, onTimeUpdate);
    seekInput?.removeEventListener(`input`, onSeekInput);
    progress?.removeEventListener(`mousemove`, onProgressMove);
    chapterSpan?.remove();
    label.remove();
  };
}

function generateThumbnailVtt(customerCode: string, token: string, duration: number, interval = 10): string {
  const lines = [`WEBVTT`, ``,];
  for (let t = 0; t < duration; t += interval) {
    const end = Math.min(t + interval, duration);
    lines.push(`${formatVttTime(t)} --> ${formatVttTime(end)}`);
    lines.push(`https://customer-${customerCode}.cloudflarestream.com/${token}/thumbnails/thumbnail.jpg?time=${t}s&height=108`);
    lines.push(``);
  }
  return lines.join(`\n`);
}

const PLYR_OPTIONS = {
  invertTime: false,
  fullscreen: { iosNative: false, },
  controls: [`play-large`, `play`, `progress`, `current-time`, `duration`, `mute`, `volume`, `captions`, `settings`, `pip`, `fullscreen`,],
};

function setupPlayerPost(
  player: any,
  video: HTMLVideoElement,
  chapters: { time: number; label: string }[] | undefined,
  duration: number | undefined
) {
  (video as any)._plyr = player;
  if (window.matchMedia(`(max-width: 768px)`).matches) player.volume = 1;
  player.once(`playing`, () => video.closest(`.plyr`)?.classList.add(`plyr--has-played`));

  if (chapters?.length && duration) {
    const prog = video.closest(`.plyr`)?.querySelector(`.plyr__progress`) as HTMLElement;
    if (prog) {
      for (const ch of chapters) {
        if (ch.time === 0) continue;
        const div = document.createElement(`div`);
        div.className = `chapter-divider`;
        div.style.left = `${(ch.time / duration) * 100}%`;
        prog.appendChild(div);
      }
    }
  }

  if (chapters?.length) {
    const plyrEl = video.closest(`.plyr`) as HTMLElement;
    if (plyrEl) {
      (video as any)._chapterCleanup = setupChapterLabel(plyrEl, video, chapters, duration ?? 0);
    }
  }
}

function setupSeekBuffer(video: HTMLVideoElement) {
  const MIN_BUFFER_AHEAD = 3;
  const getBufferedAhead = () => {
    for (let i = 0; i < video.buffered.length; i++) {
      if (video.buffered.start(i) <= video.currentTime + 0.1) {
        return video.buffered.end(i) - video.currentTime;
      }
    }
    return 0;
  };
  let wasPaused = false;
  const onSeeking = () => { wasPaused = video.paused; };
  const onSeeked = () => {
    if (wasPaused) return;
    if (getBufferedAhead() >= MIN_BUFFER_AHEAD) return;
    video.pause();
    const onProgress = () => {
      if (getBufferedAhead() >= MIN_BUFFER_AHEAD) {
        video.removeEventListener(`progress`, onProgress);
        video.play();
      }
    };
    video.addEventListener(`progress`, onProgress);
  };
  video.addEventListener(`seeking`, onSeeking);
  video.addEventListener(`seeked`, onSeeked);

  (video as any)._seekCleanup = () => {
    video.removeEventListener(`seeking`, onSeeking);
    video.removeEventListener(`seeked`, onSeeked);
  };
}

export default function Player({
  token,
  customerCode,
  duration,
  poster,
  placeholder,
  chapters,
}: {
  token: string,
  customerCode?: string,
  duration?: number,
  poster?: string,
  placeholder?: string,
  chapters?: { time: number; label: string }[],
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [ready, setReady,] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Skip reinit if player already exists (e.g. after router.refresh())
    if ((video as any)._plyr) {
      setReady(true);
      return;
    }

    const hlsUrl = `https://customer-${customerCode}.cloudflarestream.com/${token}/manifest/video.m3u8`;

    let vttUrl: string | undefined;
    if (customerCode && duration) {
      const vttBlob = new Blob([generateThumbnailVtt(customerCode, token, duration),], { type: `text/vtt`, });
      vttUrl = URL.createObjectURL(vttBlob);
    }

    let destroyed = false;

    Promise.all([
      import(`plyr`),
      import(`hls.js`),
      import(`plyr/dist/plyr.css`),
    ]).then(([{ default: Plyr, }, { default: Hls, },]) => {
      if (destroyed) return;

      let player: InstanceType<typeof Plyr> | null = null;
      let hls: InstanceType<typeof Hls> | null = null;

      if (Hls.isSupported()) {
        hls = new Hls({
          lowLatencyMode: false,
          maxBufferLength: 30,
          backBufferLength: 30,
          maxBufferHole: 0.1,
          nudgeMaxRetry: 8,
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
          if (destroyed) return;
          const heights = Array.from(new Set(data.levels.map((l: { height: number }) => l.height))).sort((a, b) => b - a);
          player = new Plyr(video, {
            ...PLYR_OPTIONS,
            previewThumbnails: vttUrl ? { enabled: true, src: vttUrl, } : { enabled: false, },
            i18n: { qualityLabel: { 0: `auto`, }, },
            quality: {
              default: 0,
              options: [0, ...heights,],
              forced: true,
              onChange: (quality: number) => {
                if (!hls) return;
                if (quality === 0) {
                  hls.currentLevel = -1;
                } else {
                  hls.currentLevel = hls.levels.findIndex((l: { height: number }) => l.height === quality);
                }
              },
            },
          });
          setReady(true);
          setupPlayerPost(player, video, chapters, duration);
        });
      } else if (video.canPlayType(`application/vnd.apple.mpegurl`)) {
        video.src = hlsUrl;
        player = new Plyr(video, {
          ...PLYR_OPTIONS,
          previewThumbnails: vttUrl ? { enabled: true, src: vttUrl, } : { enabled: false, },
        });
        setReady(true);
        setupPlayerPost(player, video, chapters, duration);
      }

      (video as any)._hls = hls;
      setupSeekBuffer(video);
    });

    return () => {
      destroyed = true;
      if (vttUrl) URL.revokeObjectURL(vttUrl);
      (video as any)._chapterCleanup?.();
      (video as any)._seekCleanup?.();
      (video as any)._hls?.destroy();
      (video as any)._plyr?.destroy();
    };
  }, [token, customerCode, duration, chapters,]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    let active = false;
    const onWrapperClick = () => { active = true; };
    const onDocClick = (e: MouseEvent) => { if (!wrapper.contains(e.target as Node)) active = false; };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!active) return;
      const p = (videoRef.current as any)?._plyr;
      if (!p) return;
      if (e.key === `f`) { e.preventDefault(); p.fullscreen?.toggle(); }
      else if (e.key === ` ` || e.key === `k`) { e.preventDefault(); p.togglePlay(); }
      else if (e.key === `m`) { e.preventDefault(); p.toggleMute(); }
      else if (e.key === `ArrowRight`) { e.preventDefault(); p.forward(5); }
      else if (e.key === `ArrowLeft`) { e.preventDefault(); p.rewind(5); }
    };
    wrapper.addEventListener(`click`, onWrapperClick);
    document.addEventListener(`click`, onDocClick);
    document.addEventListener(`keydown`, onKeyDown);
    return () => {
      wrapper.removeEventListener(`click`, onWrapperClick);
      document.removeEventListener(`click`, onDocClick);
      document.removeEventListener(`keydown`, onKeyDown);
    };
  }, []);

  // Double-tap to seek (sides) and play/pause (center) on mobile
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let hideTimeout: ReturnType<typeof setTimeout> | null = null;

    function formatSeek(seconds: number): string {
      const abs = Math.abs(seconds);
      const sign = seconds < 0 ? `-` : `+`;
      if (abs < 60) return `${sign}${abs}s`;
      const m = Math.floor(abs / 60);
      const s = abs % 60;
      return s ? `${sign}${m}m ${s}s` : `${sign}${m}m`;
    }

    function getIndicator(): HTMLElement {
      const container = wrapper!.querySelector(`.plyr`) || wrapper!;
      let el = container.querySelector(`.seek-indicator`) as HTMLElement;
      if (!el) {
        el = document.createElement(`div`);
        el.className = `seek-indicator`;
        container.appendChild(el);
      }
      return el;
    }

    function showIndicator(seconds: number, side: `left` | `right`) {
      if (hideTimeout) clearTimeout(hideTimeout);
      const el = getIndicator();
      el.textContent = formatSeek(seconds);
      el.style.left = side === `left` ? `15%` : ``;
      el.style.right = side === `right` ? `15%` : ``;
      el.style.opacity = `1`;
      hideTimeout = setTimeout(() => { el.style.opacity = `0`; }, 600);
    }

    let lastTap = 0;
    let lastX = 0;
    let lastZone: `left` | `right` | `center` = `center`;
    let totalSeek = 0;
    const DELTAS = [10, 10, 10, 15, 15, 60,];
    let tapCount = 0;

    function getZone(x: number, width: number): `left` | `right` | `center` {
      const third = width / 3;
      if (x < third) return `left`;
      if (x > third * 2) return `right`;
      return `center`;
    }

    let controlsVisibleBeforeTap = false;

    const onTouchStart = (e: TouchEvent) => {
      const plyr = (videoRef.current as any)?._plyr;
      controlsVisibleBeforeTap = plyr ? !plyr.elements.container.classList.contains(`plyr--hide-controls`) : false;
      if (Date.now() - lastTap < 300) {
        e.preventDefault();
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      const p = (videoRef.current as any)?._plyr;
      if (!p) return;

      const isFullscreen = p.fullscreen?.active;

      const touch = e.changedTouches[0];
      const now = Date.now();
      const rect = wrapper.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const zone = getZone(x, rect.width);

      const tappedControl = !!(e.target as HTMLElement).closest(`.plyr__controls`);

      const hideIfVisible = () => {
        if (tappedControl) return;
        if (controlsVisibleBeforeTap && !p.paused) {
          setTimeout(() => p.toggleControls(false), 50);
        }
      };

      if (!isFullscreen) {
        if (now - lastTap < 300) {
          p.fullscreen?.enter();
          lastTap = 0;
        } else {
          lastTap = now;
          hideIfVisible();
        }
        tapCount = 0;
        totalSeek = 0;
        lastZone = `center`;
        return;
      }

      if (zone === `center`) {
        if (now - lastTap < 300 && lastZone === `center`) {
          e.preventDefault();
          p.togglePlay();
          lastTap = 0;
        } else {
          lastTap = now;
          lastZone = `center`;
          hideIfVisible();
        }
        tapCount = 0;
        totalSeek = 0;
        return;
      }

      const sameZone = zone === lastZone;

      if (now - lastTap < 300 && sameZone) {
        e.preventDefault();
        const delta = DELTAS[Math.min(tapCount, DELTAS.length - 1)];
        totalSeek += delta;
        tapCount++;
        if (zone === `left`) {
          p.rewind(delta);
          showIndicator(-totalSeek, `left`);
        } else {
          p.forward(delta);
          showIndicator(totalSeek, `right`);
        }
        lastTap = now;
      } else {
        tapCount = 0;
        totalSeek = 0;
        lastTap = now;
        lastX = x;
        lastZone = zone;
        hideIfVisible();
      }
    };

    wrapper.addEventListener(`touchstart`, onTouchStart, { passive: false, capture: true, });
    wrapper.addEventListener(`touchend`, onTouchEnd);
    return () => {
      if (hideTimeout) clearTimeout(hideTimeout);
      wrapper.querySelector(`.seek-indicator`)?.remove();
      wrapper.removeEventListener(`touchstart`, onTouchStart);
      wrapper.removeEventListener(`touchend`, onTouchEnd);
    };
  }, []);

  return (
    <div ref={wrapperRef} style={{ aspectRatio: `16/9`, width: `100%`, position: `relative`, background: placeholder ? `url(${placeholder}) center/cover no-repeat` : `#000`, }}>
      <video ref={videoRef} poster={poster} playsInline style={{ width: `100%`, height: `100%`, }} />
      {!ready && (
        <div style={{ position: `absolute`, inset: 0, display: `flex`, alignItems: `center`, justifyContent: `center`, }}>
          <div className="w-14 h-14 rounded-full border-4 border-white/30 border-t-white animate-spin" />
        </div>
      )}
    </div>
  );
}
